
import {cloneObject, stringify} from './functions';
import Responder from './responder';

class State extends Responder{

    constructor() {
        // bootstrap stuff
        super();

        this.name = 'state';
        this.state = new Map();
        this.listenerNamespaces = new Map();


        this.stateTree = new Map();
        this.stateWatchers = new Map();
        this.previousState = new Map();


        this.forceGlobalUpdate = false;
        this.someUpdate = true;
        this.timerId = null;

        this.doTrigger = true;

    }

    getProperties(name, value, map = new Map()){

        if(value !== null && value instanceof Object && 
                ! (value instanceof Array) && ! (value instanceof Map) && 
                !  (value instanceof Set) ){
            for(let key of Object.keys(value)){
                map = this.getProperties(name + '.' + key, value[key], map);
            }
        }else{
            if(value instanceof Array){
                map.set(name, value.slice());
            }else{
                map.set(name, value);
            }
        }

        return map;
    }


    set(name, value) {

        if(stringify(this.get(name)) === stringify(value)){
            return;
        }

        const state = new Map(this.state);
        let watchList = [];

        // remove the node if set
        this.state.delete(name);

        // remove any set children for this node
        for(const [k, v] of state){
            if(k.length > name.length && k.substring(0, name.length+1) === name+'.'){
                this.state.delete(k);
                if(v !== '__NOT_SET__'){
                    watchList.push(k);
                }
            }
        }
        // remove any set parents for this node (will recompile when calling get)
        const parts = name.split('.');

        let fn = [];
        for(let i = 0; i < parts.length-1; i++){
            fn.push(parts[i]);
            this.state.delete(fn.join('.'));
        }

        const map = this.getProperties(name, value);


        for(const [key, value] of map) {
            const oldValue = (state.has(key))? state.get(key): null;
            
            let changed = true;
            if(value instanceof Array && oldValue instanceof Array){
                
                if(value.length === oldValue.length){
                    changed = false;
                    for(let i = 0; i < value.length; i++){
                        if(value[i] !== oldValue[i]){
                            changed = true;
                            break;
                        }
                    }
                }
            }else if(oldValue === value){
                changed = false;
            }

            if(value == null){
                this.state.delete(key);
                if(oldValue === '__NOT_SET__'){
                    changed = false;
                }
            }else{
                this.state.set(key, value);
            }
            if(changed){
                watchList.push(key);
            }
        };

        // this.addParentsToWatchlist(watchList, state);
        if(! this.doTrigger){
            return watchList;
        }

        this.triggerWatchlist(watchList);

    }

    triggerWatchlist(watchList){
        if(watchList.length > 0){
            let done = new Set();
            for(const key of watchList){
                if(! done.has(key)){
                    //console.log('STATE: ' + key + ' = ' + this.state.get(key));
                    this.trigger(key);
                    done.add(key);
                }
            }
        }
    }

    setDefault(name, value){
        const cur = this.get(name);
        if(cur === null){
            this.set(name, value);
        }
    }

    get(namespace, default_value=null){
        return this.getData(namespace, default_value);
    }

    getData(namespace, default_value=null){
        let value = this.getValue(namespace, this.state);
        return value===null? default_value: value;
    }

    getValue(name, map){
        if(map.has(name)){
            const v = map.get(name);
            if(v === '__NOT_SET__'){
                return null;
            }
            return cloneObject(v);
        }else{
            let result = {};
            let found = false;
            for(const [k, v] of map){
                if(v === '__NOT_SET__'){
                    continue;
                }

                if(name === '*' || (k.length > name.length && 
                        k.substring(0, name.length+1) === name+'.')){
                    const value = cloneObject(v);
                    found = true;
                    const path = (name==='*')? k.split('.'): 
                            k.substring(name.length+1).split('.');
                    let r = result;
                    for(let i = 0; i < path.length; i++){
                        if(typeof r[path[i]] === 'undefined'){
                            r[path[i]] = (i < path.length-1)? {}: value;
                        }
                        r = r[path[i]];
                    }
                }
            }
            if(found){ // save the result for later re-use
                map.set(name, result);
                return result;
            }
            map.set(name, '__NOT_SET__');
            return null;
        }
    }

    // if you need to set several properties at once, you can use this.
    setState(state) {
        this.doTrigger = false;
        const watchList = [];
        for(let key of Object.keys(state)){
            const wl = this.set(key, state[key]);
            if(wl){
                watchList.push(...wl);
            }
        }
        this.doTrigger = true;
        this.triggerWatchlist(watchList);
    }

    addListener(callback, namespace='*'){
        let id = super.addListener(callback);
        this.listenerNamespaces.set(id, namespace);
        return id;
    }

    getCallback(id_or_callback){
        if(typeof id_or_callback === 'number'){
            return this.listeners.get(id_or_callback);
        }
        return id_or_callback;
    }

    removeListener(id_or_callback, namespace=null){
        if(namespace!==null){
            const callback = this.getCallback(id_or_callback);
            for(const [id, ns] of this.listenerNamespaces){
                if(ns === namespace){
                    const cb = this.listeners.get(id);
                    if(callback === cb){
                        super.removeListener(id);
                        this.listenerNamespaces.delete(id);
                    }
                }
            }
        }else{
            let id = super.removeListener(id_or_callback);
            this.listenerNamespaces.delete(id);
        }
    }

    /**
     * triggers all listeners in the current namespace
     */
    trigger(namespace='*'){

        if(namespace==='*' || namespace===''){
            return super.trigger();
        }

        let callbackIds = [];
        for(const [id, ns] of this.listenerNamespaces){
            if(namespace.length <= ns.length){
                // It's important to also include the dot, else 'value' and 'value2' could match
                if(ns.substring(0, namespace.length+1) === namespace+'.' || ns === namespace || namespace === '*'){
                    callbackIds.push(id);
                }
            }else if(namespace.substring(0, ns.length+1) === ns+'.'){
                callbackIds.push(id);
            }
        }

        for(const id of callbackIds){
            this.applyCallback(id);
        }

    }

}

let stateInstance = new State();
export default stateInstance;


// WEBPACK FOOTER //
// ./src/zviz3/state.js